/* LT-Prelims-01 — Prelim Library edit / create page. Mirrors LibraryEdit
 * but with a much simpler schema (item_name, section, client_category,
 * unit_type, default_rate, default_quantity OR default_percentage, display
 * order, is_active). New items POST to /api/prelim-library; existing items
 * PATCH on every change with the same debounced auto-save the rest of the
 * app uses.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { PageHeader, Card, PrimaryButton } from "../components/UI";

const SECTIONS = [
  ["staff", "Staff"],
  ["welfare", "Welfare"],
  ["plant", "Plant, Materials Handling & Attendances"],
  ["scaffolding", "Scaffolding — Internal & External"],
  ["fencing", "Temporary Fencing, Hoarding & Security"],
  ["cleaning", "Cleaning"],
  ["misc", "Misc Items"],
];
const CATEGORIES = [
  ["scaffolding", "Scaffolding"],
  ["welfare", "Welfare Facilities"],
  ["management", "Management"],
  ["sundries", "Sundries"],
];
const UNIT_TYPES = [
  ["weekly", "weekly (qty × weeks × rate)"],
  ["item", "item (qty × rate, default qty=1)"],
  ["nr_weeks", "qty × weeks × rate"],
  ["qty_only", "qty × rate (no weeks — skips, haulage, etc.)"],
  ["percentage_weekly", "% × weekly (qty is %, rate per week)"],
  ["percentage_section", "% of section subtotal (e.g. scaffolding 10% uplift)"],
];

function defaultDoc() {
  return {
    item_name: "",
    section: "misc",
    client_category: "sundries",
    unit_type: "item",
    default_rate: 0,
    default_quantity: null,
    default_percentage: null,
    display_order: 99,
    is_active: true,
  };
}

export default function PrelimLibraryEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";
  const [doc, setDoc] = useState(defaultDoc());
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    api.get(`/prelim-library/${id}`)
      .then((r) => setDoc(r.data))
      .catch(() => { toast.error("Failed to load item"); navigate("/prelim-library"); })
      .finally(() => setLoading(false));
  }, [id, isNew, navigate]);

  const set = (patch) => setDoc((d) => ({ ...d, ...patch }));

  // Auto-set default client_category when section changes (matches the seed
  // mapping). Doesn't override an explicit user choice — only when section
  // changes AND category equals the prior section's default.
  const onSectionChange = (next) => {
    const sectionDefault = {
      staff: "management", welfare: "welfare", scaffolding: "scaffolding",
      plant: "sundries", fencing: "sundries", cleaning: "sundries", misc: "sundries",
    };
    const oldSectionDefault = sectionDefault[doc.section];
    set({
      section: next,
      ...(doc.client_category === oldSectionDefault
        ? { client_category: sectionDefault[next] }
        : {}),
    });
  };

  const save = async () => {
    if (!doc.item_name.trim()) {
      toast.error("Item name is required");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const r = await api.post("/prelim-library", doc);
        toast.success(`Created “${r.data.item_name}”`);
        navigate("/prelim-library");
      } else {
        const r = await api.put(`/prelim-library/${id}`, doc);
        setDoc(r.data);
        toast.success("Saved");
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deletePermanent = async () => {
    if (!window.confirm(`Permanently delete “${doc.item_name}”? This can't be undone. Existing tenders are unaffected.`)) return;
    try {
      await api.delete(`/prelim-library/${id}/permanent`);
      toast.success("Deleted");
      navigate("/prelim-library");
    } catch (e) {
      console.error(e);
      toast.error("Delete failed");
    }
  };

  if (loading) {
    return <div className="app-container py-12 text-center text-ink-3" style={{ fontSize: 12 }}>Loading…</div>;
  }

  return (
    <div className="app-container py-8">
      <button
        onClick={() => navigate("/prelim-library")}
        className="inline-flex items-center gap-1 text-ink-3 hover:text-ink mb-3"
        style={{ fontSize: 12 }}
      >
        <ArrowLeft size={12} /> Back to Prelim Library
      </button>
      <PageHeader
        title={isNew ? "New prelim library item" : doc.item_name || "Untitled item"}
        subtitle={isNew ? "Defines a master item that tenders can seed from." : "Edits don't affect existing tenders — only newly-seeded ones."}
        actions={
          <div className="flex items-center gap-2">
            {!isNew && (
              <button onClick={deletePermanent} className="inline-flex items-center gap-1 h-9 px-3 text-note-red hover:bg-note-red/5 rounded-md" style={{ fontSize: 12 }} data-testid="prelim-library-edit-delete">
                <Trash2 size={12} /> Delete
              </button>
            )}
            <PrimaryButton icon={Save} onClick={save} disabled={saving} data-testid="prelim-library-edit-save">
              {saving ? "Saving…" : isNew ? "Create" : "Save"}
            </PrimaryButton>
          </div>
        }
      />

      <Card>
        <div className="p-5 grid grid-cols-2 gap-5">
          <div className="col-span-2">
            <label className="col-head text-ink-3 block mb-1">Item name</label>
            <input
              value={doc.item_name}
              onChange={(e) => set({ item_name: e.target.value })}
              data-testid="prelim-library-edit-name"
              className="w-full h-10 px-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              style={{ fontSize: 13 }}
            />
          </div>

          <div>
            <label className="col-head text-ink-3 block mb-1">Section</label>
            <select
              value={doc.section}
              onChange={(e) => onSectionChange(e.target.value)}
              data-testid="prelim-library-edit-section"
              className="w-full h-10 px-3 rounded-md border border-line bg-surface text-ink"
              style={{ fontSize: 13 }}
            >
              {SECTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>

          <div>
            <label className="col-head text-ink-3 block mb-1">Client category</label>
            <select
              value={doc.client_category}
              onChange={(e) => set({ client_category: e.target.value })}
              data-testid="prelim-library-edit-category"
              className="w-full h-10 px-3 rounded-md border border-line bg-surface text-ink"
              style={{ fontSize: 13 }}
            >
              {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>

          <div className="col-span-2">
            <label className="col-head text-ink-3 block mb-1">Unit type</label>
            <select
              value={doc.unit_type}
              onChange={(e) => set({ unit_type: e.target.value })}
              data-testid="prelim-library-edit-unit-type"
              className="w-full h-10 px-3 rounded-md border border-line bg-surface text-ink"
              style={{ fontSize: 13 }}
            >
              {UNIT_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>

          <div>
            <label className="col-head text-ink-3 block mb-1">Default rate (£)</label>
            <input
              type="number" step="0.01"
              value={doc.default_rate ?? 0}
              onChange={(e) => set({ default_rate: Number(e.target.value) })}
              data-testid="prelim-library-edit-rate"
              className="w-full h-10 px-3 rounded-md border border-line bg-surface text-ink font-mono tnum text-right focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              style={{ fontSize: 13 }}
            />
          </div>
          <div>
            <label className="col-head text-ink-3 block mb-1">Display order</label>
            <input
              type="number" step="1"
              value={doc.display_order ?? 0}
              onChange={(e) => set({ display_order: Number(e.target.value) })}
              data-testid="prelim-library-edit-order"
              className="w-full h-10 px-3 rounded-md border border-line bg-surface text-ink font-mono tnum text-right focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              style={{ fontSize: 13 }}
            />
          </div>

          <div>
            <label className="col-head text-ink-3 block mb-1">Default quantity</label>
            <input
              type="number" step="0.5"
              value={doc.default_quantity ?? ""}
              onChange={(e) => set({ default_quantity: e.target.value === "" ? null : Number(e.target.value) })}
              placeholder="optional"
              data-testid="prelim-library-edit-default-qty"
              className="w-full h-10 px-3 rounded-md border border-line bg-surface text-ink font-mono tnum text-right focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              style={{ fontSize: 13 }}
            />
          </div>
          <div>
            <label className="col-head text-ink-3 block mb-1">Default percentage</label>
            <input
              type="number" step="0.5"
              value={doc.default_percentage ?? ""}
              onChange={(e) => set({ default_percentage: e.target.value === "" ? null : Number(e.target.value) })}
              placeholder="optional (Staff %, Scaffolding 10%)"
              data-testid="prelim-library-edit-default-pct"
              className="w-full h-10 px-3 rounded-md border border-line bg-surface text-ink font-mono tnum text-right focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              style={{ fontSize: 13 }}
            />
          </div>

          <div className="col-span-2 mt-1 rounded-md border border-line bg-surface-2/40 p-3.5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={doc.is_default_for_new_tenders !== false}
                onChange={(e) => set({ is_default_for_new_tenders: e.target.checked })}
                data-testid="prelim-library-edit-default-toggle"
                className="mt-0.5"
              />
              <span>
                <span className="text-ink block" style={{ fontSize: 13, fontWeight: 500 }}>
                  Seed on new tenders
                </span>
                <span className="text-ink-3 block mt-0.5" style={{ fontSize: 11.5 }}>
                  When ticked, this item is added automatically to every new tender's Prelims.
                  Untick to keep it in the library only — estimators can still add it per tender.
                </span>
              </span>
            </label>
          </div>
        </div>
      </Card>
    </div>
  );
}
